import { Fragment, type ReactNode } from 'react';

/**
 * Formato sencillo del cuaderno (texto plano, seguro de guardar):
 *   > líneas de un pasaje (la primera: "> 📖 1 Juan 1:1-9 (RV1960)")
 *   __subrayado__   ==resaltado==   {{nota sobre la nota}}
 */
const INLINE = /(__[^_\n]+?__|==[^=\n]+?==|\{\{[^}\n]+?\}\})/g;

function inline(text: string, key: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    const k = `${key}-${i}`;
    if (part.startsWith('__') && part.endsWith('__') && part.length > 4) return <u key={k}>{inline(part.slice(2, -2), k)}</u>;
    if (part.startsWith('==') && part.endsWith('==') && part.length > 4) return <mark key={k}>{inline(part.slice(2, -2), k)}</mark>;
    if (part.startsWith('{{') && part.endsWith('}}') && part.length > 4) {
      return (
        <span key={k} className="note-annot" role="note">
          <span aria-hidden="true">📝 </span>
          {part.slice(2, -2).trim()}
        </span>
      );
    }
    return <Fragment key={k}>{part}</Fragment>;
  });
}

/** Muestra un estudio con sus pasajes, subrayados, resaltados y notas. */
export function NoteBody({ text }: { text: string }) {
  const blocks: { quote: boolean; lines: string[] }[] = [];
  for (const line of text.split('\n')) {
    const quote = line.startsWith('>');
    const last = blocks[blocks.length - 1];
    if (last && last.quote === quote) last.lines.push(quote ? line.replace(/^>\s?/, '') : line);
    else blocks.push({ quote, lines: [quote ? line.replace(/^>\s?/, '') : line] });
  }
  return (
    <div className="note-body">
      {blocks.map((b, i) =>
        b.quote ? (
          <blockquote key={i} className="note-passage">
            {b.lines.map((l, j) => (
              <p key={j} className={j === 0 && l.startsWith('📖') ? 'note-passage__ref' : undefined}>
                {inline(l, `${i}-${j}`)}
              </p>
            ))}
          </blockquote>
        ) : (
          <p key={i} className="pre-line">
            {inline(b.lines.join('\n'), `${i}`)}
          </p>
        ),
      )}
    </div>
  );
}

/** Texto de un pasaje listo para el cuaderno. */
export function passageBlock(label: string, translation: string | null, verses: { verse: number; text: string }[]): string {
  const head = `> 📖 ${label}${translation ? ` (${translation})` : ''}`;
  return [head, ...verses.map((v) => `> ${v.verse} ${v.text}`)].join('\n');
}
